import { chatRoomPath, chatSpacePath } from "@circle-react/helpers/urlHelpers";
import { linkOptions } from "@tanstack/react-router";
import { isEmpty, isNull } from "lodash";

import { t } from "@/i18n-js/instance";
import { scrollToLoadedMessage } from "@/react/components/Chat/EmbeddableChat/VirtualizedMessageList/loadedMessageScrollers";
import { DIRECT_CHAT_ROOM } from "@/react/components/constants";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";

export const getParticipantNames = (participants: ChatRoomParticipant[]) => {
  let participantNames = "";

  participants.forEach((participant, index) => {
    if (index == participants.length - 1) {
      participantNames = participantNames + participant.name + ".";
    } else if (index == participants.length - 2) {
      participantNames = participantNames + participant.name + " and ";
    } else {
      participantNames = participantNames + participant.name + ", ";
    }
  });

  return participantNames;
};

export const scrollToMessage = (
  id: string,
  behavior: ScrollBehavior = "smooth",
  callback?: () => void,
) => {
  const scrollTimeOut = setTimeout(() => {
    const messageElement = document.getElementById(id);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior, block: "center" });
    } else {
      // A virtualized list doesn't mount rows away from the viewport.
      scrollToLoadedMessage(String(id), behavior);
    }
    callback?.();
  }, 100);
  return () => clearTimeout(scrollTimeOut);
};

export const getRole = (
  communityMemberId: string | number,
  roles: Record<string, any>,
) => {
  for (const [key, value] of Object.entries(roles || [])) {
    if (Array.isArray(value) && value.includes(communityMemberId)) {
      return key;
    }
  }
  return undefined;
};

export const isMessageSent = (message: any, currentParticipant: any) =>
  message.sender.id === currentParticipant.id;

export const isLastMessageSame = (lastMessageInArr?: any, lastMessage?: any) =>
  lastMessageInArr &&
  !!lastMessageInArr.id &&
  lastMessageInArr.id > 0 &&
  lastMessageInArr?.creation_uuid === lastMessage?.creation_uuid;

export const getMessageHashFromUrl = () =>
  (window.location.hash || "").split("#message_")[1];

export const showDateChangeLine = (
  displayDayChange: boolean,
  dayChange: boolean,
) => displayDayChange && dayChange;

export interface ShowUnreadMessageLineProps {
  disableUnreadMessageLine?: boolean;
  id?: string;
  isPinned?: boolean;
  unreadMessageId?: number | null;
}

export const showUnreadMessageLine = ({
  id,
  isPinned,
  disableUnreadMessageLine,
  unreadMessageId,
}: ShowUnreadMessageLineProps) =>
  !isNull(id) &&
  !isPinned &&
  !disableUnreadMessageLine &&
  // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible.
  unreadMessageId === id;

interface ShouldHightlightMessageProps {
  id?: string;
  messageHashId?: string;
  messageHighlightId?: string;
}

export const shouldHightlightMessage = ({
  id,
  messageHashId,
  messageHighlightId,
}: ShouldHightlightMessageProps) =>
  (messageHashId && id == messageHashId) ||
  (messageHighlightId && id == messageHighlightId);

interface ShouldHightlightThreadMessageProps {
  id?: number;
  isParentMessage?: boolean;
  messageHighlightId?: number;
}

export const shouldHightlightThreadMessage = ({
  id,
  messageHighlightId,
  isParentMessage,
}: ShouldHightlightThreadMessageProps) =>
  !isParentMessage && messageHighlightId && id == messageHighlightId;

export const getDeleteConfirmationMessageText = (repliesCount = 0) => {
  if (repliesCount > 0) {
    return `${t("messaging.delete_message.description")} ${t(
      "messaging.delete_message.threads",
    )}`;
  }
  return t("messaging.delete_message.description");
};

const getChatRoomUrl = (chatRoom: any, message: any) => {
  if (isEmpty(chatRoom)) return "";
  const { embedded_space, uuid } = chatRoom;
  const { slug } = embedded_space || {};
  const { embedded } = message;

  return embedded ? chatSpacePath({ spaceSlug: slug }) : chatRoomPath({ uuid });
};

export const getChatRoomLinkOptions = (
  chatRoom: any,
  message: any,
  isParentMessage: boolean,
) => {
  const { embedded_space, uuid } = chatRoom;
  const { slug } = embedded_space || {};
  const { embedded } = message;

  const hash = isParentMessage
    ? getChatRoomMessageRedirectionHash(message)
    : getChatThreadMessageRedirectionHash(message);

  const search = isParentMessage ? undefined : { message_id: message.id };

  const linkProps = embedded
    ? linkOptions({
        to: "/c/$spaceSlug",
        params: { spaceSlug: slug },
      })
    : linkOptions({
        to: "/messages/$uuid",
        params: { uuid },
      });

  return {
    ...linkProps,
    hash,
    search,
  };
};

const getChatRoomMessageRedirectionHash = (message: any) =>
  `message_${message.id}`;

const getChatThreadMessageRedirectionHash = (message: any) =>
  `message_${message.parent_message_id}`;

export const getChatRoomMessageRedirectionUrl = (
  chatRoom: any,
  message: any,
) => {
  const chatRoomUrl = getChatRoomUrl(chatRoom, message);
  return `${chatRoomUrl}#message_${message.id}`;
};

export const findMissingParticipantIds = (
  messages: ChatRoomMessage[],
  participants: ChatRoomParticipant[],
) => {
  const missingParticipantIds: number[] = [];

  messages.forEach(message => {
    const { chat_room_participant_id: participantId } = message;

    // Only websocket payloads carry the sender, and the message block renders
    // straight from it, so there is nothing to fetch for those messages.
    if (message.sender) return;

    const sender = participants.find(
      participant => participant?.id === participantId,
    );

    if (!sender && !missingParticipantIds.includes(participantId))
      missingParticipantIds.push(participantId);
  });

  return missingParticipantIds;
};

export const isDirectChatRoom = (chatRoom: any) =>
  Boolean(chatRoom?.chat_room_kind === DIRECT_CHAT_ROOM);

export const isChatRoomAdminOrModerator = (chatRoom: any) => {
  const { current_participant: currentParticipant = {} } = chatRoom || {};
  return Boolean(currentParticipant?.admin || currentParticipant?.moderator);
};

export const getPortalTargetId = (
  isMessagingPage: boolean,
  isRenderedOnHeader: boolean,
) => {
  let portalTargetId: string | null = null;

  if (isMessagingPage) {
    portalTargetId = "fullscreen-modal-body";
  } else if (isRenderedOnHeader) {
    portalTargetId = "chat-threads-header";
  }

  return portalTargetId;
};
